import { useEffect, useState } from 'react';
import {
  ByCategoryCollectionContentElementContentModel,
  ByCategoryCollectionContentPropertiesModel,
  CollectionContentResponseModel,
} from '@/services/platform/api/model';
import { cn, LinksToLinkMap, ModuleProperties, Image, useLocaleStore } from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { ShopDiscoverBanner } from '@/components/shared/shop';
import { getCollectionById } from '@/services/platform/data/shop/catalog';

export const ByCategoryCollectionContent = (props: ModuleProperties) => {
  const content = props.content as ByCategoryCollectionContentElementContentModel | undefined;
  const properties = content?.properties as ByCategoryCollectionContentPropertiesModel | undefined;
  const locale = useLocaleStore(state => state.locale);
  // CMS `categoryBehaviour`: `true` links the banner to the collection's first associated category
  // instead of the collection itself.
  const linkToAssociatedCategory = properties?.categoryBehaviour ?? false;
  const pickedCollection = properties?.collectionPicker?.[0] as CollectionContentResponseModel | undefined;

  // Hooks run before any early return — see the note below the state declaration.
  const [collection, setCollection] = useState<CollectionContentResponseModel | undefined>(
    pickedCollection?.properties ? pickedCollection : undefined
  );

  useEffect(() => {
    if (!pickedCollection?.id) return;
    getCollectionById(pickedCollection.id, locale).then(result => {
      if (result) setCollection(result);
    });
  }, [pickedCollection?.id]);

  /*
   * `if (!content) return null` used to sit above these hooks (and again below them). A component
   * that returns before calling its hooks renders a different number of them per pass, which is what
   * React throws "Rendered fewer hooks than expected" over the moment `content` goes from set to
   * unset. Nothing here needs `content`, so the guard simply moves after them.
   */
  if (!content) return null;

  const bannerTitle = properties?.title;
  const bannerDescription = properties?.description;
  const bannerLink = LinksToLinkMap(properties?.link ?? undefined);
  const bannerImage = {
    src: collection?.properties?.imageUrl,
    alt: collection?.name,
  } as Image;

  /*
   * The id comes from the CMS picker, not from the fetched collection.
   *
   * `collection` is only populated once `getCollectionById` resolves, so on the first render it is
   * `undefined` — and a template literal turns that into the *string* `"undefined"`, producing a
   * real, clickable `/collection_undefined` link. Clicking it navigates to a page whose id parses as
   * `"undefined"`, which then 404s against the CMS. The picker already carries the same id
   * synchronously (the effect above uses it), so there is no window where it is unknown; the fetch is
   * only needed for the image and the name.
   *
   * Still guarded: an unpicked or misconfigured module has no id at all, and leaving the authored
   * href alone is better than pointing the banner at a URL that cannot resolve.
   */
  const collectionId = collection?.id ?? pickedCollection?.id;
  // Unlike `collectionId`, the associated category is only known once the collection is resolved —
  // until then (or when none is set) the banner falls back to the collection link.
  const collectionCategoryId = collection?.properties?.associatedCategory?.[0]?.id;
  if (bannerLink && linkToAssociatedCategory && collectionCategoryId) {
    bannerLink.href = `${props?.context?.page?.route?.path}/category_${collectionCategoryId}`;
  } else if (bannerLink && collectionId) {
    bannerLink.href = `${props?.context?.page?.route?.path}/collection_${collectionId}`;
  }

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        // See byCategoryCategoryContent/index.tsx for what `group` and the `last:mb-[...]`
        // override do — same mechanism, shared by every ShopDiscoverBanner-rendering module.
        base: cn([
          'module__by-category-collection-content group mb-1.25',
          'last:mb-[calc(0.3125rem-max(1rem,env(safe-area-inset-bottom)))]!',
        ]),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ShopDiscoverBanner
        image={bannerImage}
        title={bannerTitle}
        description={bannerDescription}
        link={bannerLink}
      />
    </ModuleWrapper>
  );
};
